import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiViewEntityResponse, ApiViewResponseError, ApplicationModel, CountryModel, RoleModel } from '../typings';
import { getBodyErrors } from '../services/ApiService';
import { MapApplicationModel, MapCountryModel, MapRoleModel } from '../mappings'
import identityService from '../services/IdentityService';

export enum CachedEntity {
    Country,
    Application,
    Role
}

export interface CacheLookup {
    id: number,
    key: string,
    label: string,
    description: string
}

type CacheSlice = {
    cache: {
        [E in CachedEntity]: CacheLookup[]
    },
    pending: {
        [E in CachedEntity]: boolean
    },
    // Set once an entity loads successfully, so one that resolves to an empty list isn't re-fetched every render.
    loaded: {
        [E in CachedEntity]: boolean
    }
}

const initialState: CacheSlice = {
    cache: {
        [CachedEntity.Country]: [],
        [CachedEntity.Application]: [],
        [CachedEntity.Role]: []
    },
    pending: {
        [CachedEntity.Country]: false,
        [CachedEntity.Application]: false,
        [CachedEntity.Role]: false
    },
    loaded: {
        [CachedEntity.Country]: false,
        [CachedEntity.Application]: false,
        [CachedEntity.Role]: false
    }
}

// A session persisted before `loaded` existed rehydrates without it; restore the shape defensively.
const markLoaded = (state: CacheSlice, entity: CachedEntity) => {
    if (!state.loaded) {
        state.loaded = {
            [CachedEntity.Country]: false,
            [CachedEntity.Application]: false,
            [CachedEntity.Role]: false
        };
    }
    state.loaded[entity] = true;
}

const CacheSlice = createSlice({
    name: 'cache',
    initialState,
    reducers: {
        cacheReset: () => initialState,
    },
    extraReducers(builder) {
        builder
            .addCase(fetchCountries.pending, (state) => {
                state.pending[CachedEntity.Country] = true;
            })
            .addCase(fetchCountries.fulfilled, (state, action) => {
                const results = action.payload.data;
                state.cache[CachedEntity.Country] = results.map((country) => MapCountryModel(country));
                state.pending[CachedEntity.Country] = false;
                markLoaded(state, CachedEntity.Country);
            })
            .addCase(fetchCountries.rejected, (state) => {
                // Not marked loaded: a failed fetch stays retryable rather than empty for the session.
                state.pending[CachedEntity.Country] = false;
            })
            .addCase(fetchApplications.pending, (state) => {
                state.pending[CachedEntity.Application] = true;
            })
            .addCase(fetchApplications.fulfilled, (state, action) => {
                const results = action.payload.data;
                state.cache[CachedEntity.Application] = results.map((application) => MapApplicationModel(application));
                state.pending[CachedEntity.Application] = false;
                markLoaded(state, CachedEntity.Application);
            })
            .addCase(fetchApplications.rejected, (state) => {
                state.pending[CachedEntity.Application] = false;
            })
            .addCase(fetchRoles.pending, (state) => {
                state.pending[CachedEntity.Role] = true;
            })
            .addCase(fetchRoles.fulfilled, (state, action) => {
                const results = action.payload.data;
                state.cache[CachedEntity.Role] = results.map((role) => MapRoleModel(role));
                state.pending[CachedEntity.Role] = false;
                markLoaded(state, CachedEntity.Role);
            })
            .addCase(fetchRoles.rejected, (state) => {
                state.pending[CachedEntity.Role] = false;
            })
    }
});

export const { cacheReset } = CacheSlice.actions;

export const fetchCountries = createAsyncThunk<ApiViewEntityResponse<CountryModel[]>, undefined, { rejectValue: ApiViewResponseError[] }>
    ('cache/fetchCountries', async (_, { rejectWithValue }) => {
        try {
            const response = await identityService.getCountries();
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const fetchApplications = createAsyncThunk<ApiViewEntityResponse<ApplicationModel[]>, undefined, { rejectValue: ApiViewResponseError[] }>
    ('cache/fetchApplications', async (_, { rejectWithValue }) => {
        try {
            const response = await identityService.getApplications();
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const fetchRoles = createAsyncThunk<ApiViewEntityResponse<RoleModel[]>, undefined, { rejectValue: ApiViewResponseError[] }>
    ('cache/fetchRoles', async (_, { rejectWithValue }) => {
        try {
            const response = await identityService.getRoles();
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export default CacheSlice.reducer;
